import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Paper,
  MobileStepper,
  Tab,
  Tabs,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from "@mui/material";
import { convertImageToBase64, isValidDoubleJSON, formatVideoGatewayURL } from "../../utils";
import CheckList from "./CheckList";
import { useTheme } from "@mui/material/styles";
import ReviewPatrol from "./ReviewPatrol";
import { useGetList } from "react-admin";
import { API, graphqlOperation } from 'aws-amplify';

// gql query to get the specific checkpoint data
const getCheckpointQuery = /* GraphQL */ `
query GetCheckpoint($id: ID!) {
    getCheckpoint(id: $id) {
      id
      name
      checklist
      camera {
        http_stream
        video_gateway
        camera_asset_id
      }
    }
  }
`;

const PatrolView = () => {
  const [searchParams] = useSearchParams();

  // Core flow
  const [checkpointIds, setCheckpointIds] = useState([]);
  const [currentCheckpoint, setCurrentCheckpoint] = useState(null);
  const [currentChecklist, setCurrentChecklist] = useState([]);
  const [activeStep, setActiveStep] = useState(0);

  // Persisted per checkpoint: { [checkpointId]: string[] }
  const [selectionsByCheckpoint, setSelectionsByCheckpoint] = useState({});
  const [selectedChecklist, setSelectedChecklist] = useState([]);

  // Misc UI/data
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [routeName, setRouteName] = useState("unKnown");
  const [scheduleId, setScheduleId] = useState("");
  const [patrolId, setPatrolId] = useState("");
  const [operatorId, setOperatorId] = useState("");
  const [startTime, setStartTime] = useState("");
  const [tabIndex, setTabIndex] = useState(0);
  const [note, setNote] = useState("");
  const [reportIncidentNote, setReportIncidentNote] = useState("");
  const [incidentTypeId, setIncidentTypeId] = useState("");
  const [reviewPatrolAvailable, setReviewPatrolAvailable] = useState(false);

  // This can collect multiple versions; we'll upsert by checkpoint_id
  const [reviewPatrolList, setReviewPatrolList] = useState([]);

  const theme = useTheme();
  const { data: incidentTypes, isError: incidentTypesErr } = useGetList("incidentTypes");

  // Decode initial params and load first checkpoint
  useEffect(() => {
    const encodedData = searchParams.get("data");
    if (!encodedData) return;

    try {
      const decodedData = JSON.parse(atob(decodeURIComponent(encodedData)));
      const cpIds = decodedData.checkpoint_ids;
      setRouteName(decodedData.route_name);
      setPatrolId(decodedData.patrol_id);
      setScheduleId(decodedData.schedule_id);
      setOperatorId(decodedData.operator_id);
      setStartTime(decodedData.start_time);
      setCheckpointIds(cpIds);
      fetchCheckpoint(cpIds[0]);
    } catch (error) {
      console.error("Error decoding checkpoint data:", error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Fetch a checkpoint and restore its selections
  const fetchCheckpoint = async (checkpointId) => {
    if (!checkpointId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await API.graphql(
        graphqlOperation(getCheckpointQuery, { id: checkpointId })
      );

      const data = response.data.getCheckpoint;

      let sortedItems = [];
      if (isValidDoubleJSON(data.checklist)) {
        const parsedChecks = JSON.parse(JSON.parse(data.checklist));
        sortedItems = parsedChecks.items
          .sort((a, b) => a.order - b.order)
          .map((i) => i.item);
      }
      setCurrentChecklist(sortedItems);
      setCurrentCheckpoint(data);

      // Restore saved selections; also intersect with current checklist in case items changed
      const restored = (selectionsByCheckpoint[checkpointId] || []).filter((x) =>
        sortedItems.includes(x)
      );
      setSelectedChecklist(restored);
    } catch (err) {
      console.error("Error fetching checkpoint data:", err);
      setError("Failed to fetch checkpoint");
    } finally {
      setLoading(false);
    }
  };

  // Build review data (uses selectedChecklist)
  const getReviewPatrolData = async () => {
    const { id, name, camera = {} } = currentCheckpoint || {};
    const { video_gateway, camera_asset_id } = camera;
    let imageBase64 = "";

    if (video_gateway && camera_asset_id) {
      try {
        const parsedUrl = formatVideoGatewayURL(video_gateway, camera_asset_id);
        imageBase64 = await convertImageToBase64(parsedUrl);
        if (imageBase64 === null || !String(imageBase64).includes("data:image")) {
          console.error("Error converting image to base64");
        }
      } catch (error) {
        console.error("Error fetching image:", error);
      }
    }

    const baseReviewPatrol = {
      checkpoint_id: id,
      checkpoint_name: name,
      order: activeStep,
      notes: note,
      completed_at: new Date().toISOString(),
      image_base64: imageBase64,
      checklist_results: {
        items: currentChecklist.map((check, idx) => ({
          item: check,
          order: idx,
          checked: selectedChecklist.includes(check),
        })),
      },
    };

    if (incidentTypeId !== "") {
      return {
        ...baseReviewPatrol,
        incident_name:
          (incidentTypes || []).find((inciType) => inciType.id === incidentTypeId)?.name || "",
        incident_type_id: incidentTypeId,
        incident_note: reportIncidentNote || "",
      };
    }
    return baseReviewPatrol;
  };

  // Save current selections for current checkpoint
  const persistCurrentSelections = () => {
    const cpId = currentCheckpoint?.id;
    if (cpId) {
      setSelectionsByCheckpoint((prev) => ({
        ...prev,
        [cpId]: selectedChecklist,
      }));
    }
  };

  // ---- DEDUPE HELPERS ----
  // Upsert by checkpoint_id (replace if exists, else append)
  const upsertReview = (list, review) => {
    const idx = list.findIndex((r) => r.checkpoint_id === review.checkpoint_id);
    if (idx > -1) {
      const copy = list.slice();
      copy[idx] = review;
      return copy;
    }
    return [...list, review];
  };

  // Produce a deduped list ordered by checkpointIds (for final review)
  const getDedupedOrderedReviews = (list) => {
    const map = new Map();
    list.forEach((r) => map.set(r.checkpoint_id, r)); // last write wins
    return checkpointIds.map((id) => map.get(id)).filter(Boolean);
  };

  // Navigation
  const handleNext = async () => {
    // Ensure we save selections even if user didn't toggle after loading
    persistCurrentSelections();

    const reviewData = await getReviewPatrolData();
    if (!reviewData) return;

    // UPSERT instead of push to avoid duplicates
    setReviewPatrolList((prev) => upsertReview(prev, reviewData));

    if (activeStep < checkpointIds.length - 1) {
      const nextStep = activeStep + 1;
      setActiveStep(nextStep);
      const nextId = checkpointIds[nextStep];
      fetchCheckpoint(nextId);
      setNote("");
      setIncidentTypeId("");
      setReportIncidentNote("");
      setTabIndex(0);
    } else {
      setReviewPatrolAvailable(true);
    }
  };

  const handleBack = () => {
    if (reviewPatrolAvailable) {
      setReviewPatrolAvailable(false);
      return;
    }

    if (activeStep > 0) {
      persistCurrentSelections();

      const prevStep = activeStep - 1;
      const leavingId = checkpointIds[activeStep]; // the one we were on
      setActiveStep(prevStep);
      const prevId = checkpointIds[prevStep];

      // If the last entry is exactly the one we're leaving, drop it; otherwise leave list as-is.
      setReviewPatrolList((prev) => {
        if (!prev.length) return prev;
        const last = prev[prev.length - 1];
        if (last.checkpoint_id === leavingId) return prev.slice(0, -1);
        return prev;
      });

      fetchCheckpoint(prevId);
      setNote("");
    }
  };

  // Checklist selection handler (persist per checkpoint)
  const handleSelectionChange = (nextSelected) => {
    setSelectedChecklist(nextSelected);
    const cpId = currentCheckpoint?.id;
    if (cpId) {
      setSelectionsByCheckpoint((prev) => ({
        ...prev,
        [cpId]: nextSelected,
      }));
    }
  };

  const handleTabChange = (_event, index) => setTabIndex(index);

  // Prepare deduped & ordered data when in review screen
  const dedupedOrderedReviews = getDedupedOrderedReviews(reviewPatrolList);

  return reviewPatrolAvailable ? (
    <ReviewPatrol
      data={{
        patrol_id: patrolId,
        schedule_id: scheduleId,
        operator_id: operatorId,
        start_time: startTime,
        end_time: new Date().toISOString(),
        // Use deduped, ordered list to avoid duplicates in final payload
        checkpoints: dedupedOrderedReviews,
      }}
      onBack={handleBack}
    />
  ) : (
    <Box display="flex" p={2} flexDirection="column" height="100vh">
      <Typography
        variant="h4"
        px={1}
        sx={{ visibility: currentCheckpoint?.name ? "visible" : "hidden" }}
      >
        {routeName}
      </Typography>

      <Box display="flex" alignItems="center">
        <MobileStepper
          variant="progress"
          steps={checkpointIds.length}
          position="static"
          activeStep={activeStep}
          sx={{
            borderRadius: "8px",
            width: "97%",
            "& .MuiLinearProgress-root": {
              width: "100%",
              height: 20,
              borderRadius: "8px",
              backgroundColor: "rgba(255, 255, 255, 0.2)",
            },
            "& .MuiLinearProgress-bar": {
              borderRadius: "8px",
            },
          }}
        />
        <Typography variant="subtitle2">
          {`${activeStep + 1} / ${checkpointIds.length}`}
        </Typography>
      </Box>

      <Paper
        sx={{
          p: 1,
          m: 1,
          borderRadius: 2,
          flexGrow: 1,
          textAlign: loading && "center",
        }}
      >
        {loading ? (
          <CircularProgress />
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : currentCheckpoint ? (
          <Box display="flex" height="100%">
            <Box
              sx={{
                width: "70%",
                flexGrow: 1,
                borderRadius: "10px",
                overflow: "hidden",
                boxShadow: 3,
                backgroundColor: "black",
              }}
            >
              <iframe
                src={currentCheckpoint.camera?.http_stream}
                width="100%"
                height="100%"
                frameBorder="0"
                allowFullScreen
                title="camera"
              />
            </Box>

            <Box width="30%" ml={2} display="flex" flexDirection="column">
              <Paper
                sx={{
                  backgroundColor: theme.palette.background.default,
                  p: 2,
                  borderRadius: 2,
                  flexGrow: 1,
                  overflow: "auto",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Box p={2}>
                  <Box sx={{ borderBottom: "1px solid grey", width: "100%" }}>
                    <Typography variant="text">CHECKLIST</Typography>
                  </Box>
                  <Box>
                    {currentChecklist.length ? (
                      <CheckList
                        items={currentChecklist}
                        selected={selectedChecklist}
                        onSelectionChange={handleSelectionChange}
                      />
                    ) : (
                      <Typography component="p">No checklist available.</Typography>
                    )}
                  </Box>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={handleBack}
                    disabled={activeStep === 0 && !reviewPatrolAvailable}
                    sx={{ mr: 1, textTransform: "none" }}
                  >
                    Back
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleNext}
                    sx={{ textTransform: "none" }}
                  >
                    Next
                  </Button>
                </Box>
              </Paper>

              <Paper sx={{ p: 1, backgroundColor: theme.palette.background.default, borderRadius: 2 }}>
                <Box>
                  <Tabs value={tabIndex} onChange={handleTabChange}>
                    <Tab label="NOTES" />
                    <Tab label="REPORT INCIDENT" />
                  </Tabs>

                  <Box>
                    {tabIndex === 0 && (
                      <TextField
                        variant="outlined"
                        multiline
                        rows={4}
                        fullWidth
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                      />
                    )}
                    {tabIndex === 1 && (
                      <Box>
                        <TextField
                          variant="outlined"
                          multiline
                          rows={4}
                          fullWidth
                          value={reportIncidentNote}
                          onChange={(e) => setReportIncidentNote(e.target.value)}
                        />
                        <FormControl fullWidth sx={{ my: 2 }} required>
                          <InputLabel>Select incident type</InputLabel>
                          <Select
                            value={incidentTypeId}
                            onChange={(e) => setIncidentTypeId(e.target.value)}
                            label="Select incident type"
                          >
                            {(incidentTypesErr ? [] : incidentTypes || []).map((incitype) => (
                              <MenuItem key={incitype.id} value={incitype.id}>
                                {incitype.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Paper>
            </Box>
          </Box>
        ) : (
          <Typography>No checkpoint data available.</Typography>
        )}
      </Paper>
    </Box>
  );
};

export default PatrolView;
export { PatrolView };
